"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, Boxes, Dna, Layers, Network, ShieldAlert, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
interface DomainItem {
  id: string;
  code: string;
  title: string;
  iconName: string;
  pointOne: string;
  pointTwo: string;
  standard: string;
}
const studyDomains: DomainItem[] = [{
  id: "domain-1",
  code: "DOM-01 // CELL-REC",
  title: "Cellular signaling & receptor binding",
  iconName: "Network",
  pointOne: "Quantify GPCR activation thresholds and downstream kinase cascades.",
  pointTwo: "Evaluate competitive ligand kinetics in stable cell lines.",
  standard: "HPLC ≥99.2%"
}, {
  id: "domain-2",
  code: "DOM-02 // META-GLUC",
  title: "Metabolic regulation & glucose homeostasis",
  iconName: "Activity",
  pointOne: "Monitor incretin-receptor modulation in isolated pancreatic islets.",
  pointTwo: "Track glucose uptake markers across cultured myocyte systems.",
  standard: "MS Verified"
}, {
  id: "domain-3",
  code: "DOM-03 // ECM-REGEN",
  title: "Tissue regeneration & extracellular matrix kinetics",
  iconName: "Layers",
  pointOne: "Analyze fibroblast migration assays and procollagen deposition.",
  pointTwo: "Measure matrix metalloproteinase inhibition in tissue scaffolds.",
  standard: "Lyophilized Pure"
}, {
  id: "domain-4",
  code: "DOM-04 // NEURO-PATH",
  title: "Neurobiology & cognitive pathway studies",
  iconName: "Dna",
  pointOne: "Assess synaptogenesis pathways in primary hippocampal neurons.",
  pointTwo: "Probe blood-brain barrier transport dynamics in transwell models.",
  standard: "Analytical Grade"
}, {
  id: "domain-5",
  code: "DOM-05 // AMP-ASSAY",
  title: "Antimicrobial peptide (AMP) assays",
  iconName: "ShieldAlert",
  pointOne: "Determine minimum inhibitory concentrations (MIC) on bacterial strains.",
  pointTwo: "Quantify membrane permeabilization using fluorescent dye uptake.",
  standard: "HPLC ≥99.0%"
}, {
  id: "domain-6",
  code: "DOM-06 // PROT-IMMUN",
  title: "Proteomics & immunological probes",
  iconName: "Boxes",
  pointOne: "Synthesize calibrated epitope targets for selective antibody profiling.",
  pointTwo: "Validate cytokine release assays in stimulated PBMC cultures.",
  standard: "Lot Tested"
}];
function getDomainIcon(name: string) {
  switch (name) {
    case "Network":
      return <Network className="size-5 text-primary" strokeWidth={1.75} />;
    case "Activity":
      return <Activity className="size-5 text-primary" strokeWidth={1.75} />;
    case "Layers":
      return <Layers className="size-5 text-primary" strokeWidth={1.75} />;
    case "Dna":
      return <Dna className="size-5 text-primary" strokeWidth={1.75} />;
    case "ShieldAlert":
      return <ShieldAlert className="size-5 text-primary" strokeWidth={1.75} />;
    case "Boxes":
      return <Boxes className="size-5 text-primary" strokeWidth={1.75} />;
    default:
      return <Network className="size-5 text-primary" strokeWidth={1.75} />;
  }
}
export default function Domains() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.07
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 10
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="study-areas-covered" data-nav="light" className="bg-background text-foreground py-14 md:py-20 border-b border-border">
      <div className="max-w-[1088px] mx-auto px-6 md:px-8">
        {/* Header Block */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-10 border-b border-border">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-block w-2 h-2 rounded-full bg-primary" />
              <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                {"Research Scope // RUO Standards"}
              </span>
            </div>
            <h2 className="font-default text-3xl md:text-4xl font-semibold tracking-tight text-foreground">
              Investigational domains
            </h2>
            <p className="mt-3 text-base text-muted-foreground leading-relaxed">
              Comprehensive categorization of scientific research domains using pure peptide reagents for laboratory-verified analytical testing.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Button asChild variant="outline" size="sm" className="font-medium text-xs tracking-wider uppercase border-border hover:border-primary hover:text-primary transition-colors">
              <Link to="/ShopPage">
                <span>View catalogue</span>
                <ArrowRight className="size-3.5" />
              </Link>
            </Button>
          </div>
        </div>

        {/* 3x2 Clinical Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-10">
          {studyDomains.map((domain, index) => <motion.div key={domain.id} variants={itemVariants} data-index={index}>
              <Card className="h-full border border-border bg-card hover:border-primary/60 transition-[border-color,box-shadow] duration-200 rounded-md shadow-xs flex flex-col justify-between">
                <div>
                  <CardHeader className="p-5 pb-3">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="size-9 rounded-md bg-muted flex items-center justify-center border border-border">
                        {getDomainIcon(domain.iconName)}
                      </div>
                      <span className="font-mono text-xs text-muted-foreground font-medium bg-muted/60 px-2 py-0.5 rounded border border-border">
                        {domain.standard}
                      </span>
                    </div>

                    <div className="font-mono text-[11px] text-muted-foreground tracking-wider mb-1">
                      {domain.code}
                    </div>

                    <CardTitle className="font-default text-lg font-semibold text-foreground leading-snug">
                      {domain.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="p-5 pt-1">
                    <ul className="space-y-2.5 text-xs md:text-sm text-foreground/80 leading-relaxed border-t border-border/70 pt-3.5">
                      <li className="flex items-start gap-2">
                        <span className="inline-block mt-1.5 size-1.5 rounded-full bg-primary shrink-0" />
                        <span>{domain.pointOne}</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="inline-block mt-1.5 size-1.5 rounded-full bg-primary shrink-0" />
                        <span>{domain.pointTwo}</span>
                      </li>
                    </ul>
                  </CardContent>
                </div>

                <div className="px-5 pb-4 pt-0">
                  <div className="border-t border-border/50 pt-3 flex items-center justify-between text-xs font-mono text-muted-foreground">
                    <span>In-vitro parameter</span>
                    <span className="text-primary font-medium">Valid reagent</span>
                  </div>
                </div>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Footer verification note */}
        <div className="mt-10 p-4 rounded-md border border-border bg-muted/50 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs">
          <div className="flex items-center gap-2">
            <span className="size-2 rounded-full bg-primary" />
            <span className="font-mono font-medium text-foreground">
              {"Regulatory Notice: Certified for Research Use Only (RUO)."}
            </span>
          </div>
          <span className="text-muted-foreground">
            {"All compounds ship with lot-specific HPLC & MS analytical sheets."}
          </span>
        </div>
      </div>
    </section>;
}